<template>
  <div ref="cursorRef" class="cursor" :class="{ 'cursor--hidden': isTouch }">
    <div ref="dotRef" class="cursor__dot"></div>
    <div ref="ringRef" class="cursor__ring"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { gsap } from "gsap";

const cursorRef = ref(null);
const dotRef = ref(null);
const ringRef = ref(null);

const isTouch = ref(false);

const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
const dotPos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
const ringPos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

let magnetTarget = null;
let tickerId = null;

function onMouseMove(e) {
  mouse.x = e.clientX;
  mouse.y = e.clientY;
}

function onMouseDown() {
  gsap.to(ringRef.value, {
    scale: 0.6,
    duration: 0.15,
    ease: "power3.out",
  });
}

function onMouseUp() {
  gsap.to(ringRef.value, {
    scale: 1,
    duration: 0.4,
    ease: "elastic.out(1, 0.3)",
  });
}

function checkMagnetic() {
  const magnets = document.querySelectorAll("[data-magnetic]");
  let closest = null;
  let closestDist = Infinity;

  magnets.forEach((el) => {
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dist = Math.hypot(mouse.x - cx, mouse.y - cy);
    const threshold = Math.max(rect.width, rect.height) * 0.8;

    if (dist < threshold && dist < closestDist) {
      closest = { el, cx, cy, rect, dist, threshold };
      closestDist = dist;
    }
  });

  return closest;
}

function tick() {
  dotPos.x += (mouse.x - dotPos.x) * 0.8;
  dotPos.y += (mouse.y - dotPos.y) * 0.8;

  const mag = checkMagnetic();

  if (mag) {
    if (magnetTarget !== mag.el) {
      magnetTarget = mag.el;
      gsap.to(ringRef.value, {
        width: mag.rect.width + 20,
        height: mag.rect.height + 20,
        borderRadius: "12px",
        duration: 0.3,
        ease: "power3.out",
      });
    }
    const pull = 1 - mag.dist / mag.threshold;
    const targetX = mouse.x + (mag.cx - mouse.x) * pull * 0.4;
    const targetY = mouse.y + (mag.cy - mouse.y) * pull * 0.4;
    ringPos.x += (targetX - ringPos.x) * 0.2;
    ringPos.y += (targetY - ringPos.y) * 0.2;
  } else {
    if (magnetTarget) {
      magnetTarget = null;
      gsap.to(ringRef.value, {
        width: 40,
        height: 40,
        borderRadius: "50%",
        duration: 0.4,
        ease: "elastic.out(1, 0.4)",
      });
    }
    ringPos.x += (mouse.x - ringPos.x) * 0.15;
    ringPos.y += (mouse.y - ringPos.y) * 0.15;
  }

  if (dotRef.value) {
    dotRef.value.style.transform = `translate(${dotPos.x}px, ${dotPos.y}px) translate(-50%, -50%)`;
  }
  if (ringRef.value) {
    ringRef.value.style.transform = `translate(${ringPos.x}px, ${ringPos.y}px) translate(-50%, -50%)`;
  }
}

function handleMouseEnterInteractive() {
  gsap.to(ringRef.value, {
    scale: 1.8,
    opacity: 0.6,
    duration: 0.3,
    ease: "power3.out",
  });
  gsap.to(dotRef.value, {
    scale: 0.5,
    duration: 0.3,
    ease: "power3.out",
  });
}

function handleMouseLeaveInteractive() {
  gsap.to(ringRef.value, {
    scale: 1,
    opacity: 1,
    duration: 0.4,
    ease: "elastic.out(1, 0.4)",
  });
  gsap.to(dotRef.value, {
    scale: 1,
    duration: 0.4,
    ease: "elastic.out(1, 0.4)",
  });
}

function bindInteractives() {
  const interactives = document.querySelectorAll(
    "a, button, [data-magnetic], .pilar-card, .jornada-step, .cta__link"
  );
  interactives.forEach((el) => {
    el.addEventListener("mouseenter", handleMouseEnterInteractive);
    el.addEventListener("mouseleave", handleMouseLeaveInteractive);
  });
}

function unbindInteractives() {
  const interactives = document.querySelectorAll(
    "a, button, [data-magnetic], .pilar-card, .jornada-step, .cta__link"
  );
  interactives.forEach((el) => {
    el.removeEventListener("mouseenter", handleMouseEnterInteractive);
    el.removeEventListener("mouseleave", handleMouseLeaveInteractive);
  });
}

let bindTimer = null;

onMounted(() => {
  isTouch.value = window.matchMedia("(pointer: coarse)").matches;
  if (isTouch.value) return;

  window.addEventListener("mousemove", onMouseMove, { passive: true });
  window.addEventListener("mousedown", onMouseDown);
  window.addEventListener("mouseup", onMouseUp);

  tickerId = gsap.ticker.add(tick);

  bindTimer = setTimeout(bindInteractives, 1000);

  const observer = new MutationObserver(() => {
    unbindInteractives();
    bindInteractives();
  });
  observer.observe(document.body, { childList: true, subtree: true });
});

onUnmounted(() => {
  if (isTouch.value) return;

  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mousedown", onMouseDown);
  window.removeEventListener("mouseup", onMouseUp);

  if (tickerId) gsap.ticker.remove(tick);
  if (bindTimer) clearTimeout(bindTimer);

  unbindInteractives();
});
</script>

<style>
.cursor {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  pointer-events: none;
}

.cursor--hidden {
  display: none;
}

.cursor__dot {
  position: absolute;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  mix-blend-mode: difference;
  will-change: transform;
}

.cursor__ring {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 1.5px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  mix-blend-mode: difference;
  will-change: transform;
  transition: opacity 0.3s;
}
</style>
